import { Component, type ErrorInfo, type ReactNode } from 'react';
import { Alert } from '../Alert';
import { Box } from '../Box';
import { Button } from '../Button';

export interface FeatureErrorBoundaryProps {
  children: ReactNode;
  fallbackTitle?: string;
  onReset?: () => void;
}

interface FeatureErrorBoundaryState {
  hasError: boolean;
}

// React error boundary — ADR 0004 step 6 (the error-catching mechanism has no MUI
// primitive). Caller: H1, to isolate one search tab's failure from its siblings.
// The fallback reuses the Alert (it carries role="alert"), Button and Box façades.
export class FeatureErrorBoundary extends Component<
  FeatureErrorBoundaryProps,
  FeatureErrorBoundaryState
> {
  override state: FeatureErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): FeatureErrorBoundaryState {
    return { hasError: true };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Surface the failure so a broken tab doesn't fail silently.
    console.error(error, info);
  }

  private readonly handleReset = (): void => {
    this.setState({ hasError: false });
    this.props.onReset?.();
  };

  override render(): ReactNode {
    if (this.state.hasError) {
      return (
        <Alert severity="error">
          {/* TODO: i18n */}
          {this.props.fallbackTitle ?? 'No se pudo cargar esta sección.'}
          <Box sx={{ mt: 1 }}>
            <Button variant="outlined" size="small" onClick={this.handleReset}>
              Reintentar
            </Button>
          </Box>
        </Alert>
      );
    }
    return this.props.children;
  }
}
